/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Chat State Reducer
 * 
 * Handles all state updates for the chat system using React's useReducer pattern.
 */

import { ChatState, ChatAction } from './types';

export function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'SET_EXPANDED':
      return { ...state, isExpanded: action.payload };
    case 'SET_SIDEBAR_WIDTH':
      return { ...state, sidebarWidth: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_EXECUTING':
      return { ...state, isExecuting: action.payload };
    case 'SET_SELECTED_AGENT':
      return { ...state, selectedAgent: action.payload };
    case 'ADD_MESSAGE':
      return { ...state, messages: [...state.messages, action.payload] };
    case 'SET_MESSAGES':
      return { ...state, messages: action.payload };
    case 'UPDATE_MESSAGE':
      return {
        ...state,
        messages: state.messages.map(message =>
          (message.id === action.payload.messageId || message.uuid === action.payload.messageId)
            ? { ...message, ...action.payload.updates }
            : message
        )
      };
    case 'UPDATE_MESSAGE_CONTENT':
      return {
        ...state,
        messages: state.messages.map(message =>
          (message.id === action.payload.messageId || message.uuid === action.payload.messageId)
            ? {
                ...message,
                content: action.payload.isIncremental 
                  ? message.content + action.payload.content
                  : action.payload.content
              }
            : message
        )
      };
    case 'ADD_TOOL_CALL':
      const updatedMessages = state.messages.map(message =>
        (message.id === action.payload.messageId || message.uuid === action.payload.messageId)
          ? {
              ...message,
              toolCalls: [...(message.toolCalls || []), action.payload.toolCall]
            }
          : message
      );

      return {
        ...state,
        messages: updatedMessages
      };
    case 'UPDATE_TOOL_CALL':
      return {
        ...state,
        messages: state.messages.map(message =>
          (message.id === action.payload.messageId || message.uuid === action.payload.messageId)
            ? {
                ...message,
                toolCalls: (message.toolCalls || []).map(toolCall =>
                  toolCall.id === action.payload.toolCallId
                    ? { ...toolCall, ...action.payload.updates }
                    : toolCall
                )
              }
            : message
        )
      };
    case 'ADD_ENTITY_LINK':
      return {
        ...state,
        messages: state.messages.map(message =>
          (message.id === action.payload.messageId || message.uuid === action.payload.messageId)
            ? {
                ...message,
                entityLinks: [...(message.entityLinks || []), action.payload.entityLink]
              }
            : message
        )
      };
    case 'ADD_SUB_AGENT_CALL':
      return {
        ...state,
        messages: state.messages.map(message =>
          (message.id === action.payload.messageId || message.uuid === action.payload.messageId)
            ? {
                ...message,
                subAgentCalls: [...(message.subAgentCalls || []), action.payload.subAgentCall]
              }
            : message
        )
      };
    case 'UPDATE_SUB_AGENT_CALL':
      return {
        ...state,
        messages: state.messages.map(message =>
          (message.id === action.payload.messageId || message.uuid === action.payload.messageId)
            ? {
                ...message,
                subAgentCalls: (message.subAgentCalls || []).map(call =>
                  call.agentName === action.payload.agentName
                    ? { ...call, ...action.payload.updates }
                    : call
                )
              }
            : message
        )
      };
    case 'UPDATE_ALL_SUB_AGENT_CALLS':
      return {
        ...state,
        messages: state.messages.map(message =>
          (message.id === action.payload.messageId || message.uuid === action.payload.messageId)
            ? {
                ...message,
                subAgentCalls: (message.subAgentCalls || []).map(call =>
                  call.status === 'started' // Only update calls that are still in progress
                    ? { ...call, ...action.payload.updates }
                    : call
                )
              }
            : message
        )
      };
    case 'SET_DB_SESSION':
      return {
        ...state,
        dbSessionId: action.payload.dbSessionId,
        sessionKey: action.payload.sessionKey,
        sessionTitle: action.payload.sessionTitle
      };
    case 'SET_SESSION_TITLE':
      return {
        ...state,
        sessionTitle: action.payload
      };
    case 'SET_HONCHO_STATE':
      return { 
        ...state, 
        workspaceId: action.payload.workspaceId,
        honchoSessionId: action.payload.honchoSessionId,
        peerId: action.payload.peerId
      };
    case 'SET_INITIALIZED':
      return { ...state, isInitialized: action.payload };
    case 'UPDATE_MESSAGE_EXECUTION_STATE':
      return {
        ...state,
        messages: state.messages.map(message => 
          (message.id === action.payload.messageId || message.uuid === action.payload.messageId) || 
          (message.actionPlan?.planId === action.payload.messageId) // Also match by planId for backward compatibility
            ? {
                ...message,
                executionState: {
                  isExecuting: false,
                  isExecuted: false,
                  executionId: null,
                  executionStatus: null,
                  executionResults: null,
                  ...message.executionState,
                  ...action.payload.executionState
                }
              }
            : message
        )
      };
    case 'SET_EXECUTION_STATE':
      return {
        ...state,
        currentExecutionId: action.payload.executionId,
        canTerminateExecution: action.payload.canTerminate ?? state.canTerminateExecution
      };
    case 'SET_PLAN':
      return {
        ...state,
        plan: action.payload
      };
    case 'UPDATE_PLAN_TASK':
      if (!state.plan) {
        return state;
      }
      return {
        ...state,
        plan: {
          ...state.plan,
          tasks: state.plan.tasks.map(task =>
            task.id === action.payload.taskId
              ? { ...task, ...action.payload.updates, updatedAt: new Date() }
              : task
          ),
          updatedAt: new Date()
        }
      };
    case 'RESET_CHAT':
      return { 
        ...state, 
        messages: [],
        dbSessionId: undefined,
        sessionKey: undefined,
        honchoSessionId: undefined,
        isInitialized: false,
        isExecuting: false,
        currentExecutionId: undefined,
        canTerminateExecution: false,
        plan: null
      };
    default:
      return state;
  }
}
